import { useTranslation } from "react-i18next";
import SelectField from "./controls/SelectField";
import Row from "./layout/Row";
import Col from "./layout/Col";
import ObjectField from "./controls/ObjectField";
import Grid from "./layout/Grid";
import TextField from "./controls/TextField";
import TextareaField from "./controls/TextareaField";
import { isInvalid } from "../utils/forms";
import Pagination from "./controls/Pagination";
import Button from "./controls/Button";
import Panel from "./layout/Panel";
import Label from "./controls/Label";
import { Formik } from "formik";
import { ResiduoTratamientoSchema } from "../data/ResiduoTratamientoSchema";
import AmmountField from "./controls/AmmountField";
import QuestionField from "./controls/QuestionField";
import { DestinoResiduoTratamiento, TipoTramite } from "../utils/enums";
import { PersonaJuridicaSchema } from "../data/PersonaJuridicaSchema";
import { useDialog } from "./controls/Dialog";
import Alert from "./controls/Alert";

export function ResiduoTratamientoForm({
  value,
  page,
  pages,
  select,
  append,
  replace,
  remove,
  disabled,
  tramiteForm,
  unidadCantidad,
  destinosResiduosTratamiento,
  frecuenciasRetiros,
}) {
  const { t } = useTranslation();
  const dialog = useDialog({ showing: false, variant: "warning" });
  const handleNombreChange = (form) => (event) => {
    form.handleChange(event);
  };
  const handleCantidadChange = (form) => (event) => {
    form.handleChange(event);
  };
  const handleDescripcionChange = (form) => (event) => {
    form.handleChange(event);
  };
  const handleDestinoChange = (form) => (event) => {
    if (
      form.values.conPeligrosidad ||
      event.target.value?.codigo !== DestinoResiduoTratamiento.EXTERNO
    ) {
      form.setFieldValue("tratador", null);
      form.setFieldTouched("tratador", false, true);
      form.setFieldValue("transportista", null);
      form.setFieldTouched("transportista", false, true);
      form.setFieldValue("frecuenciaRetiros", null);
      form.setFieldTouched("frecuenciaRetiros", false, true);
      form.setFieldValue("tratamiento", null);
      form.setFieldTouched("tratamiento", false, true);
    } else {
      form.setFieldValue(
        "tratador",
        PersonaJuridicaSchema.cast(undefined, { assert: false }),
        true,
      );
      form.setFieldTouched("tratador", true, true);
      form.setFieldValue(
        "transportista",
        PersonaJuridicaSchema.cast(undefined, { assert: false }),
        true,
      );
      form.setFieldTouched("transportista", true, true);
      form.setFieldValue("frecuenciaRetiros", null);
      form.setFieldTouched("frecuenciaRetiros", true, true);
      form.setFieldValue("tratamiento", null);
      form.setFieldTouched("tratamiento", true, true);
    }
    form.handleChange(event);
  };
  const handleConPeligrosidadChange = (form) => (event) => {
    if (
      event.target.checked ||
      form.values?.destino?.codigo !== DestinoResiduoTratamiento.EXTERNO
    ) {
      form.setFieldValue("tratador", null);
      form.setFieldTouched("tratador", false, true);
      form.setFieldValue("transportista", null);
      form.setFieldTouched("transportista", false, true);
      form.setFieldValue("frecuenciaRetiros", null);
      form.setFieldTouched("frecuenciaRetiros", false, true);
    } else {
      form.setFieldValue(
        "tratador",
        PersonaJuridicaSchema.cast(undefined, { assert: false }),
        false,
      );
      form.setFieldTouched("tratador", false, false);
      form.setFieldValue(
        "transportista",
        PersonaJuridicaSchema.cast(undefined, { assert: false }),
        false,
      );
      form.setFieldTouched("transportista", false, false);
      form.setFieldValue("frecuenciaRetiros", null);
      form.setFieldTouched("frecuenciaRetiros", false, false);
    }
    form.handleChange(event);
  };
  const handleSubmit = (values) => {
    if (
      values.conPeligrosidad &&
      !tramiteForm.values.conResiduosPeligrosos &&
      tramiteForm.values.tipo?.codigo === TipoTramite.DJG
    ) {
      dialog.display(
        t("residuoTratamientoForm.conPeligrosidad.dialog.title"),
        <p>{t("residuoTratamientoForm.conPeligrosidad.dialog.message")}</p>,
        t("residuoTratamientoForm.conPeligrosidad.dialog.accept"),
        t("residuoTratamientoForm.conPeligrosidad.dialog.decline"),
        (event) => {
          if (page === -1) {
            append(values);
          } else {
            replace(values, page);
          }
          dialog.close();
        },
        (event) => {
          dialog.close();
        },
      );
    } else {
      if (page === -1) {
        append(values);
      } else {
        replace(values, page);
      }
    }
  };

  return (
    <>
      {dialog.component}
      <Formik
        initialValues={value}
        enableReinitialize={true}
        validateOnChange={true}
        validationSchema={ResiduoTratamientoSchema}
        onSubmit={handleSubmit}
      >
        {(form) => (
          <Grid as={<Panel />} className="formulario embebido">
            <Row>
              <Col>
                <Label>{t("residuoTratamientoForm.titulo")}</Label>
              </Col>
            </Row>
            <Row>
              <Col>
                <TextField
                  id="residuoTratamiento_nombre"
                  name="nombre"
                  label={t("residuoTratamiento.nombre")}
                  value={form.values?.nombre}
                  onChange={handleNombreChange(form)}
                  isInvalid={isInvalid("nombre", form)}
                  error={t(form.errors.nombre)}
                  disabled={disabled}
                />
              </Col>
            </Row>
            <Row>
              <Col lg={4}>
                <AmmountField
                  id="residuoTratamiento_cantidad"
                  name="cantidad"
                  label={t("residuoTratamiento.cantidad")}
                  value={form.values?.cantidad}
                  onChange={handleCantidadChange(form)}
                  unit={unidadCantidad}
                  isInvalid={isInvalid("cantidad", form)}
                  error={t(form.errors.cantidad)}
                  disabled={disabled}
                />
              </Col>
            </Row>
            <Row>
              <Col>
                <TextareaField
                  id="residuoTratamiento_descripcion"
                  name="descripcion"
                  label={t("residuoTratamiento.descripcion")}
                  value={form.values?.descripcion}
                  onChange={handleDescripcionChange(form)}
                  rows={12}
                  isInvalid={isInvalid("descripcion", form)}
                  error={t(form.errors.descripcion)}
                  disabled={disabled}
                />
              </Col>
            </Row>
            <Row>
              <Col>
                <SelectField
                  id="residuoTratamiento_destino"
                  name="destino"
                  label={t("residuoTratamiento.destino")}
                  value={form.values?.destino}
                  onChange={handleDestinoChange(form)}
                  options={destinosResiduosTratamiento}
                  labelAttr="nombre"
                  valueAttr="codigo"
                  emptyLabel="..."
                  isInvalid={isInvalid("destino", form)}
                  error={t(form.errors.destino)}
                  disabled={disabled}
                />
              </Col>
            </Row>
            <Row>
              <Col>
                <QuestionField
                  id="residuoTratamiento_conPeligrosidad"
                  name="conPeligrosidad"
                  label={t("residuoTratamiento.conPeligrosidad")}
                  value={form.values?.conPeligrosidad}
                  onChange={handleConPeligrosidadChange(form)}
                  isInvalid={isInvalid("conPeligrosidad", form)}
                  error={t(form.errors.conPeligrosidad)}
                  disabled={disabled}
                />
              </Col>
            </Row>
            {form.values?.conPeligrosidad &&
              tramiteForm.values.tipo?.codigo === TipoTramite.DJG && (
                <Row>
                  <Col>
                    <Alert variant="warning" style={{ fontSize: "10px" }}>
                      <p className="text-muted">
                        {t("residuoTratamientoForm.conPeligrosidad.message")}
                      </p>
                    </Alert>
                  </Col>
                </Row>
              )}
            <Row>
              <Col>
                <label>{t("residuoTratamiento.transportista")}</label>
                <ObjectField
                  id="residuoTratamiento_transportista"
                  name="transportista"
                  value={form.values?.transportista}
                  onChange={(event) => form.handleChange(event)}
                  disabled={
                    disabled ||
                    form.values.conPeligrosidad ||
                    form.values.destino?.codigo !== "EXTERNO"
                  }
                >
                  {({ value, setProperty, disabled }) => (
                    <Grid>
                      <Row>
                        <Col lg={4}>
                          <TextField
                            id="residuoTratamiento_transportista_CUIT"
                            name="transportista.CUIT"
                            label={t("personaJuridica.CUIT")}
                            value={value?.CUIT}
                            onChange={(event) => {
                              event.preventDefault();
                              setProperty("CUIT", event.target.value);
                            }}
                            isInvalid={isInvalid("transportista.CUIT", form)}
                            error={t(form.errors.transportista?.CUIT)}
                            disabled={disabled}
                          />
                        </Col>
                        <Col lg={8}>
                          <TextField
                            id="residuoTratamiento_transportista_razonSocial"
                            name="transportista.razonSocial"
                            label={t("personaJuridica.razonSocial")}
                            value={value?.razonSocial}
                            onChange={(event) => {
                              event.preventDefault();
                              setProperty("razonSocial", event.target.value);
                            }}
                            isInvalid={isInvalid(
                              "transportista.razonSocial",
                              form,
                            )}
                            error={t(form.errors.transportista?.razonSocial)}
                            disabled={disabled}
                          />
                        </Col>
                      </Row>
                    </Grid>
                  )}
                </ObjectField>
              </Col>
            </Row>
            <Row>
              <Col lg={4}>
                <SelectField
                  id="residuoTratamiento_frecuenciaRetiros"
                  name="frecuenciaRetiros"
                  label={t("residuoTratamiento.frecuenciaRetiros")}
                  value={form.values?.frecuenciaRetiros}
                  onChange={(event) => form.handleChange(event)}
                  options={frecuenciasRetiros}
                  labelAttr="nombre"
                  valueAttr="codigo"
                  emptyLabel="..."
                  isInvalid={isInvalid("frecuenciaRetiros", form)}
                  error={t(form.errors.frecuenciaRetiros)}
                  disabled={
                    disabled ||
                    form.values.conPeligrosidad ||
                    form.values.destino?.codigo !== "EXTERNO"
                  }
                />
              </Col>
            </Row>
            <Row>
              <Col>
                <label>{t("residuoTratamiento.tratador")}</label>
                <ObjectField
                  id="residuoTratamiento_tratador"
                  name="tratador"
                  value={form.values?.tratador}
                  onChange={(event) => form.handleChange(event)}
                  disabled={
                    disabled ||
                    form.values.conPeligrosidad ||
                    form.values.destino?.codigo !== "EXTERNO"
                  }
                >
                  {({ value, setProperty, disabled }) => (
                    <Grid>
                      <Row>
                        <Col lg={4}>
                          <TextField
                            id="residuoTratamiento_tratador_CUIT"
                            name="tratador.CUIT"
                            label={t("personaJuridica.CUIT")}
                            value={value?.CUIT}
                            onChange={(event) => {
                              event.preventDefault();
                              setProperty("CUIT", event.target.value);
                            }}
                            isInvalid={isInvalid("tratador.CUIT", form)}
                            error={t(form.errors.tratador?.CUIT)}
                            disabled={disabled}
                          />
                        </Col>
                        <Col lg={8}>
                          <TextField
                            id="residuoTratamiento_tratador_razonSocial"
                            name="tratador.razonSocial"
                            label={t("personaJuridica.razonSocial")}
                            value={value?.razonSocial}
                            onChange={(event) => {
                              event.preventDefault();
                              setProperty("razonSocial", event.target.value);
                            }}
                            isInvalid={isInvalid("tratador.razonSocial", form)}
                            error={t(form.errors.tratador?.razonSocial)}
                            disabled={disabled}
                          />
                        </Col>
                      </Row>
                    </Grid>
                  )}
                </ObjectField>
              </Col>
            </Row>

            <Row>
              <Col>
                <TextareaField
                  id="residuoTratamiento_tratamiento"
                  name="tratamiento"
                  label={t("residuoTratamiento.tratamiento")}
                  value={form.values.tratamiento}
                  onChange={(event) => form.handleChange(event)}
                  rows={12}
                  isInvalid={isInvalid("tratamiento", form)}
                  error={t(form.errors.tratamiento)}
                  disabled={
                    disabled ||
                    form.values.destino?.codigo !==
                      DestinoResiduoTratamiento.INTERNO ||
                    form.values.conPeligrosidad
                  }
                />
              </Col>
            </Row>
            <Row style={{ display: "flex", flexWrap: "wrap" }}>
              <Col lg={6}>
                <Pagination
                  sm
                  page={page}
                  pages={pages}
                  onChange={(event) => select(event.target.value)}
                />
              </Col>
              <Col lg={6}>
                <span
                  style={{
                    float: "right",
                    alignContent: "center",
                  }}
                >
                  {page !== -1 && (
                    <Button
                      sm
                      variant="success"
                      value={t("residuoTratamientoForm.nuevo")}
                      onClick={(event) => {
                        event.preventDefault();
                        select(-1);
                      }}
                      disabled={disabled}
                    />
                  )}
                  {page !== -1 && (
                    <Button
                      sm
                      variant="danger"
                      value={t("residuoTratamientoForm.eliminar")}
                      onClick={(event) => {
                        event.preventDefault();
                        remove(page);
                      }}
                      disabled={disabled}
                    />
                  )}
                  <Button
                    sm
                    variant="primary"
                    value={t("residuoTratamientoForm.guardar")}
                    onClick={(event) => {
                      event.preventDefault();
                      form.submitForm();
                    }}
                    disabled={disabled}
                  />
                  {page === -1 && pages > 0 && (
                    <Button
                      sm
                      variant="default"
                      value={t("residuoTratamientoForm.cancelar")}
                      onClick={(event) => {
                        event.preventDefault();
                        select(0);
                      }}
                      disabled={disabled}
                    />
                  )}
                </span>
              </Col>
            </Row>
          </Grid>
        )}
      </Formik>
    </>
  );
}
